/*
 * NotificationModal — "Gelen Kutusu"
 *
 * Header'daki Inbox tıklandığında açılan birleşik bildirim panosu.
 *  - Garson Çağrıları sekmesi: getAllWaiterCalls + acknowledge + resolve
 *    (canlı: useWaiterCallsRealtime, ses: sound1.mp3 — pending varken)
 *  - Müşteri Siparişleri sekmesi: GET /qr/orders/all + approve + delete
 *    (canlı: useQrOrderListener, ses: sound.mp3 — yeni sipariş varken)
 *
 * Bu komponent kendi çan ikonunu da render eder; Header.tsx içinde tek
 * mount noktası olarak kullanılır ve eski NewOrders + WaiterCallsWidget
 * butonlarının yerini alır.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from './ui/notify';
import {
  X,
  BellRing,
  Check,
  Clock,
  UtensilsCrossed,
  CheckCircle2,
  MapPin,
  Inbox,
  Loader2,
} from 'lucide-react';
import { base_url } from '../api/index';
import { PosLoader } from './pos/PosLoader';
import waiterCallSound from '../assets/sound1.mp3';
import qrOrderSound from '../assets/sound.mp3';
import {
  getAllWaiterCalls,
  acknowledgeWaiterCall,
  resolveWaiterCall,
  StaffWaiterCall,
  QrWaiterCalledEvent,
  QrWaiterCallStatusChangedEvent,
} from '../api/waiterCalls';
import { useWaiterCallsRealtime } from '../hook/useWaiterCallsRealtime';
import { useQrOrderListener } from '../hook/useQrOrderListener';
import { useI18n, tStatic } from '../i18n/I18nContext';

type TFn = (key: string, vars?: Record<string, string | number>) => string;

// -------------------------------------------------------------------------
// Yardımcı: zaman formatı (date-fns yerine — date-fns paketi kurulu değil)
// -------------------------------------------------------------------------
const formatAgoTr = (iso: string | null | undefined, t: TFn): string => {
  if (!iso) return '';
  const ts = new Date(iso).getTime();
  if (!Number.isFinite(ts)) return '';
  const diff = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (diff < 5) return t('time.now');
  if (diff < 60) return t('time.secAgo', { n: diff });
  const m = Math.floor(diff / 60);
  if (m < 60) return t('time.minAgo', { n: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t('time.hourAgo', { n: h });
  const d = Math.floor(h / 24);
  return t('time.dayAgo', { n: d });
};

const formatHm = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

// -------------------------------------------------------------------------
// Auth header (NewOrders.tsx ile aynı pattern)
// -------------------------------------------------------------------------
const getAuthHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

const readRestaurantId = (): number | null => {
  try {
    const raw = localStorage.getItem('restaurant_info');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const id = parsed?.id;
    return typeof id === 'number' ? id : id ? Number(id) : null;
  } catch {
    return null;
  }
};

// -------------------------------------------------------------------------
// QR sipariş tipi — backend'den dönen yapıyı flat'e çeviriyoruz
// -------------------------------------------------------------------------
interface FlatOrderItem {
  id: number | string;
  name: string;
  quantity: number;
  unit_price: number;
  type: 'stock' | 'set';
}

interface FlatOrder {
  id: number; // table_order id (approve/delete için)
  table_id: number;
  table_name: string;
  items: FlatOrderItem[];
  total: number;
  created_at: string;
}

const flattenQrTables = (tables: any[]): FlatOrder[] => {
  if (!Array.isArray(tables)) return [];
  const out: FlatOrder[] = [];
  for (const t of tables) {
    const tableOrders = Array.isArray(t?.table_orders) ? t.table_orders : [];
    for (const to of tableOrders) {
      const order = to?.order || {};
      const items: FlatOrderItem[] = [];

      if (Array.isArray(order.stocks)) {
        for (const s of order.stocks) {
          const piv = s?.pivot || s || {};
          const qty = Number(piv.quantity) || 1;
          const price = Number(s?.price) || 0;
          items.push({
            id: s?.id ?? Math.random(),
            name: s?.name || tStatic('inbox.product'),
            quantity: qty,
            unit_price: price,
            type: 'stock',
          });
        }
      }
      if (Array.isArray(order.stock_sets)) {
        for (const ss of order.stock_sets) {
          const piv = ss?.pivot || ss || {};
          const qty = Number(piv.quantity) || 1;
          const price = Number(ss?.unit_price ?? ss?.price) || 0;
          items.push({
            id: ss?.id ?? Math.random(),
            name: ss?.name || tStatic('inbox.set'),
            quantity: qty,
            unit_price: price,
            type: 'set',
          });
        }
      }

      const total = items.reduce((s, it) => s + it.quantity * it.unit_price, 0);
      out.push({
        id: Number(to?.id),
        table_id: Number(t?.id),
        table_name: t?.name || `${tStatic('common.table')} ${t?.id ?? ''}`,
        items,
        total,
        created_at: to?.created_at || order?.created_at || new Date().toISOString(),
      });
    }
  }
  return out;
};

// -------------------------------------------------------------------------
// Çağrı state güncelleme yardımcıları (WS event'leri için)
// -------------------------------------------------------------------------
const upsertCalledEvent = (
  prev: StaffWaiterCall[],
  ev: QrWaiterCalledEvent,
): StaffWaiterCall[] => {
  if (prev.some((c) => c.call_id === ev.call_id)) return prev;
  const synthesized: StaffWaiterCall = {
    call_id: ev.call_id,
    table: { id: ev.table_id, name: ev.table_name },
    status: ev.status,
    reason_code: ev.reason_code,
    reason_label: ev.reason_label,
    note: ev.note,
    assigned_waiter:
      ev.assigned_user_id && ev.assigned_user_name
        ? { id: ev.assigned_user_id, name: ev.assigned_user_name }
        : null,
    acknowledged_by: null,
    created_at: ev.created_at,
    acknowledged_at: null,
    resolved_at: null,
  };
  return [synthesized, ...prev];
};

const applyStatusChangedEvent = (
  prev: StaffWaiterCall[],
  ev: QrWaiterCallStatusChangedEvent,
): StaffWaiterCall[] => {
  if (ev.status === 'resolved') {
    return prev.filter((c) => c.call_id !== ev.call_id);
  }
  return prev.map((c) => {
    if (c.call_id !== ev.call_id) return c;
    return {
      ...c,
      status: ev.status,
      acknowledged_at: c.acknowledged_at ?? ev.occurred_at,
      acknowledged_by:
        c.acknowledged_by ??
        (ev.acknowledged_by && ev.acknowledged_by_name
          ? { id: ev.acknowledged_by, name: ev.acknowledged_by_name }
          : null),
    };
  });
};

// -------------------------------------------------------------------------
// Komponent
// -------------------------------------------------------------------------
const NotificationModal: React.FC = () => {
  const { t } = useI18n();
  const restaurantId = useMemo(readRestaurantId, []);
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('token');
  const enabled = hasToken && !!restaurantId;

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'calls' | 'orders'>('calls');

  // Garson çağrıları
  const [calls, setCalls] = useState<StaffWaiterCall[]>([]);
  /** İlk fetch (success ya da fail) tamamlandı mı. Bu bayrak SADECE bir kez
   *  true olur ve bir daha false'a dönmez. Bu sayede arka plan polling'leri
   *  yapan refetch'ler PosLoader'ı yeniden "flash"latmaz — "hâlâ dönüyor"
   *  bug'ının kök çözümü. */
  const [callsLoaded, setCallsLoaded] = useState(false);
  const [callActionInFlight, setCallActionInFlight] = useState<
    Record<number, 'ack' | 'resolve' | undefined>
  >({});

  // Müşteri siparişleri
  const [orders, setOrders] = useState<FlatOrder[]>([]);
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [orderActionInFlight, setOrderActionInFlight] = useState<
    Record<number, 'accept' | 'reject' | undefined>
  >({});

  // Tick — modal açıkken "X dk önce" etiketleri canlı
  // Performans: sadece modal açık + sayfa görünür + en az bir öğe varsa tick atıyoruz.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!isOpen) return;
    let t: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (t) return;
      t = setInterval(() => forceTick((n) => (n + 1) % 1_000_000), 30000);
    };
    const stop = () => {
      if (t) {
        clearInterval(t);
        t = null;
      }
    };
    if (typeof document !== 'undefined' && !document.hidden) start();
    const onVis = () => (document.hidden ? stop() : start());
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [isOpen]);

  // ---------------- Garson çağrıları: fetch ----------------
  // Polling çarpan referans her seferinde yeni nesne dönse de array'in içeriği
  // aynıysa setCalls'ı atlayıp tüm kart re-render'ını önlemek için helper.
  const callsAreSame = useCallback(
    (a: StaffWaiterCall[], b: StaffWaiterCall[]) => {
      if (a === b) return true;
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (
          a[i].call_id !== b[i].call_id ||
          a[i].status !== b[i].status ||
          a[i].acknowledged_at !== b[i].acknowledged_at
        ) {
          return false;
        }
      }
      return true;
    },
    [],
  );

  const fetchCalls = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await getAllWaiterCalls();
      const sorted = [...res.calls].sort((a, b) => {
        if (a.status !== b.status) return a.status === 'pending' ? -1 : 1;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });
      setCalls((cur) => (callsAreSame(cur, sorted) ? cur : sorted));
    } catch (err: any) {
      if (err?.response?.status !== 401) {
        console.warn('[Inbox] çağrı listesi alınamadı', err?.message || err);
      } else {
        setCalls([]);
      }
    } finally {
      // Bayrak bir kez true olur, bir daha false'a dönmez — spinner artık flash etmez.
      setCallsLoaded(true);
    }
  }, [enabled, callsAreSame]);

  // ---------------- QR siparişleri: fetch ----------------
  const ordersAreSame = useCallback((a: FlatOrder[], b: FlatOrder[]) => {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (
        a[i].id !== b[i].id ||
        a[i].items.length !== b[i].items.length ||
        a[i].total !== b[i].total
      ) {
        return false;
      }
    }
    return true;
  }, []);

  // Bu hesabın QR-menyu girişi yoxdursa (401/403) poll-u dayandırırıq ki, hər
  // 15s-də cloud-a uğursuz sorğu atıb konsolu doldurmasın (POS rejimində adi haldır).
  const qrAccessDeniedRef = useRef(false);
  const fetchOrders = useCallback(async () => {
    if (!enabled || qrAccessDeniedRef.current) return;
    try {
      const res = await axios.get(`${base_url}/qr/orders/all`, getAuthHeaders());
      const flat = flattenQrTables(res.data?.tables || []);
      setOrders((cur) => (ordersAreSame(cur, flat) ? cur : flat));
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        // Giriş yoxdur → bir də cəhd etmə.
        qrAccessDeniedRef.current = true;
        setOrders([]);
      } else {
        console.warn('[Inbox] qr sipariş listesi alınamadı', err?.message || err);
      }
    } finally {
      setOrdersLoaded(true);
    }
  }, [enabled, ordersAreSame]);

  // İlk yükleme
  useEffect(() => {
    if (!enabled) return;
    fetchCalls();
    fetchOrders();
  }, [enabled, fetchCalls, fetchOrders]);

  // ---------------- Realtime: garson çağrıları ----------------
  const seenCallIdsRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    calls.forEach((c) => seenCallIdsRef.current.add(c.call_id));
    // sadece mount'ta seed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useWaiterCallsRealtime({
    restaurantId: restaurantId ?? null,
    onCalled: (ev) => {
      setCalls((prev) => upsertCalledEvent(prev, ev));
      if (!seenCallIdsRef.current.has(ev.call_id)) {
        seenCallIdsRef.current.add(ev.call_id);
        toast.info(`${ev.table_name}: ${ev.reason_label || t('inbox.call')}`, {
          position: 'top-right' as any,
          autoClose: 6000,
        });
      }
    },
    onStatusChanged: (ev) => {
      setCalls((prev) => applyStatusChangedEvent(prev, ev));
    },
    onRefresh: fetchCalls,
    pollIntervalMs: 12000,
    disabled: !enabled,
  });

  // ---------------- Realtime: QR siparişleri ----------------
  useQrOrderListener(restaurantId, () => {
    fetchOrders();
    toast.info(t('inbox.toast.newOrder'), {
      position: 'top-right' as any,
      autoClose: 4000,
    });
  });

  // QR poll fallback (WS'den ek olarak — NewOrders.tsx ile aynı tempo)
  useEffect(() => {
    if (!enabled) return;
    const t = setInterval(fetchOrders, 15000);
    return () => clearInterval(t);
  }, [enabled, fetchOrders]);

  // ---------------- Sayaçlar + ses kontrolü ----------------
  const pendingCallsCount = useMemo(
    () => calls.filter((c) => c.status === 'pending').length,
    [calls],
  );
  const ordersCount = orders.length;

  // İki ayrı sound: çağrı + sipariş
  const callAudioRef = useRef<HTMLAudioElement | null>(null);
  const callIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const orderAudioRef = useRef<HTMLAudioElement | null>(null);
  const orderIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const audio = callAudioRef.current;
    if (!audio) return;
    if (pendingCallsCount > 0) {
      const tryPlay = () => audio.play().catch(() => undefined);
      tryPlay();
      if (!callIntervalRef.current) {
        callIntervalRef.current = setInterval(tryPlay, 4000);
      }
    } else {
      audio.pause();
      audio.currentTime = 0;
      if (callIntervalRef.current) {
        clearInterval(callIntervalRef.current);
        callIntervalRef.current = null;
      }
    }
    return () => {
      if (callIntervalRef.current) {
        clearInterval(callIntervalRef.current);
        callIntervalRef.current = null;
      }
    };
  }, [pendingCallsCount]);

  useEffect(() => {
    const audio = orderAudioRef.current;
    if (!audio) return;
    if (ordersCount > 0) {
      const tryPlay = () => audio.play().catch(() => undefined);
      tryPlay();
      if (!orderIntervalRef.current) {
        orderIntervalRef.current = setInterval(tryPlay, 2000);
      }
    } else {
      audio.pause();
      audio.currentTime = 0;
      if (orderIntervalRef.current) {
        clearInterval(orderIntervalRef.current);
        orderIntervalRef.current = null;
      }
    }
    return () => {
      if (orderIntervalRef.current) {
        clearInterval(orderIntervalRef.current);
        orderIntervalRef.current = null;
      }
    };
  }, [ordersCount]);

  // ---------------- Aksiyonlar: garson çağrıları ----------------
  const handleAcknowledge = useCallback(async (callId: number) => {
    setCallActionInFlight((m) => ({ ...m, [callId]: 'ack' }));
    try {
      const res = await acknowledgeWaiterCall(callId);
      setCalls((prev) => prev.map((c) => (c.call_id === callId ? res.call : c)));
      toast.success(t('inbox.toast.callAcked'), { position: 'top-right' as any, autoClose: 2500 });
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || t('inbox.toast.ackFailed');
      toast.error(msg, { position: 'top-right' as any, autoClose: 4000 });
    } finally {
      setCallActionInFlight((m) => ({ ...m, [callId]: undefined }));
    }
  }, [t]);

  const handleResolveCall = useCallback(async (callId: number) => {
    setCallActionInFlight((m) => ({ ...m, [callId]: 'resolve' }));
    try {
      await resolveWaiterCall(callId);
      setCalls((prev) => prev.filter((c) => c.call_id !== callId));
      toast.success(t('inbox.toast.callClosed'), { position: 'top-right' as any, autoClose: 2500 });
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || t('inbox.toast.closeFailed');
      toast.error(msg, { position: 'top-right' as any, autoClose: 4000 });
    } finally {
      setCallActionInFlight((m) => ({ ...m, [callId]: undefined }));
    }
  }, [t]);

  // ---------------- Aksiyonlar: QR siparişleri ----------------
  const handleApproveOrder = useCallback(async (orderId: number) => {
    setOrderActionInFlight((m) => ({ ...m, [orderId]: 'accept' }));
    try {
      await axios.post(`${base_url}/qr/orders/${orderId}/approve`, {}, getAuthHeaders());
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
      toast.success(t('inbox.toast.orderApproved'), { position: 'top-right' as any, autoClose: 2500 });
    } catch (err: any) {
      const data = err?.response?.data;
      const flat = data?.errors ? Object.values(data.errors).flat().join(' • ') : '';
      toast.error(flat || data?.message || t('inbox.toast.orderApproveFailed'), {
        position: 'top-right' as any,
        autoClose: 5000,
      });
    } finally {
      setOrderActionInFlight((m) => ({ ...m, [orderId]: undefined }));
    }
  }, [t]);

  const handleRejectOrder = useCallback(async (orderId: number) => {
    setOrderActionInFlight((m) => ({ ...m, [orderId]: 'reject' }));
    try {
      await axios.delete(`${base_url}/qr/orders/${orderId}`, getAuthHeaders());
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
      toast.info(t('inbox.toast.orderRejected'), { position: 'top-right' as any, autoClose: 2500 });
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || t('inbox.toast.orderRejectFailed');
      toast.error(msg, { position: 'top-right' as any, autoClose: 4000 });
    } finally {
      setOrderActionInFlight((m) => ({ ...m, [orderId]: undefined }));
    }
  }, [t]);

  if (!enabled) return null;

  const totalCount = pendingCallsCount + ordersCount;
  const hasAlerts = totalCount > 0;

  return (
    <>
      {/* Inbox tetikleyicisi — header'ın navbar tasarımıyla uyumlu */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        title={
          hasAlerts
            ? t('inbox.pending', { calls: pendingCallsCount, orders: ordersCount })
            : t('inbox.title')
        }
        className={`relative p-2.5 rounded-xl transition-all active:scale-95 ${
          hasAlerts
            ? 'bg-orange-50 text-[var(--primary)] hover:bg-orange-100 dark:bg-orange-500/15 dark:text-orange-300 dark:hover:bg-orange-500/25'
            : 'bg-gray-50 dark:bg-[#22252f] text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2e3a]'
        }`}
      >
        <Inbox className="w-5 h-5" />
        {hasAlerts && (
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-orange-500 animate-pulse border-2 border-white dark:border-[#1a1d27]" />
        )}
        {hasAlerts && totalCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full text-[10px] font-bold flex items-center justify-center bg-red-500 text-white ring-2 ring-white dark:ring-[#1a1d27]">
            {totalCount > 99 ? '99+' : totalCount}
          </span>
        )}
      </button>

      {/* Modal */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 sm:px-12 md:px-24">
            {/* Backdrop — çok hafif blur ("-md" çok pahalıydı), GPU dostu */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => setIsOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.97, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: 8 }}
              transition={{ type: 'spring', stiffness: 380, damping: 32, mass: 0.7 }}
              className="pos-card relative w-full max-w-5xl h-[85vh] flex flex-col bg-[#fcfcfc] dark:bg-[#0a0a0a] rounded-[2rem] shadow-2xl overflow-hidden border border-black/5 dark:border-white/[0.08]"
            >
              {/* Header */}
              <div className="flex-none px-10 pt-10 pb-6 flex items-center justify-between z-10">
                <div>
                  <h2 className="text-3xl font-semibold tracking-tight text-gray-900 dark:text-white flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/20">
                      <BellRing className="w-5 h-5 text-white" />
                    </div>
                    {t('inbox.title')}
                  </h2>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-3 rounded-full hover:bg-black/5 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all active:scale-95"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Tab kontrolü */}
              <div className="px-10 pb-6 flex-none z-10">
                <div className="inline-flex p-1 bg-black/5 dark:bg-white/5 rounded-2xl backdrop-blur-md">
                  <button
                    onClick={() => setActiveTab('calls')}
                    className={`relative px-6 py-2.5 text-sm font-medium transition-colors rounded-xl flex items-center gap-2 ${
                      activeTab === 'calls'
                        ? 'text-gray-900 dark:text-white'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                    }`}
                  >
                    {activeTab === 'calls' && (
                      <motion.div
                        layoutId="activeInboxTabBg"
                        className="absolute inset-0 bg-white dark:bg-[#1a1a1a] rounded-xl shadow-sm border border-black/5 dark:border-white/10"
                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                    <span className="relative z-10 flex items-center gap-2">
                      {t('inbox.tab.calls')}
                      {calls.length > 0 && (
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            activeTab === 'calls'
                              ? 'bg-orange-100 text-orange-600 dark:bg-orange-500/20 dark:text-orange-400'
                              : 'bg-black/5 text-gray-500 dark:bg-white/10 dark:text-gray-400'
                          }`}
                        >
                          {calls.length}
                        </span>
                      )}
                    </span>
                  </button>

                  <button
                    onClick={() => setActiveTab('orders')}
                    className={`relative px-6 py-2.5 text-sm font-medium transition-colors rounded-xl flex items-center gap-2 ${
                      activeTab === 'orders'
                        ? 'text-gray-900 dark:text-white'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                    }`}
                  >
                    {activeTab === 'orders' && (
                      <motion.div
                        layoutId="activeInboxTabBg"
                        className="absolute inset-0 bg-white dark:bg-[#1a1a1a] rounded-xl shadow-sm border border-black/5 dark:border-white/10"
                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                    <span className="relative z-10 flex items-center gap-2">
                      {t('inbox.tab.orders')}
                      {orders.length > 0 && (
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            activeTab === 'orders'
                              ? 'bg-orange-100 text-orange-600 dark:bg-orange-500/20 dark:text-orange-400'
                              : 'bg-black/5 text-gray-500 dark:bg-white/10 dark:text-gray-400'
                          }`}
                        >
                          {orders.length}
                        </span>
                      )}
                    </span>
                  </button>
                </div>
              </div>

              <div className="w-full h-px bg-gradient-to-r from-transparent via-black/5 dark:via-white/10 to-transparent flex-none" />

              {/* İçerik */}
              <div className="flex-1 overflow-y-auto px-10 py-8 inbox-scroll">
                {activeTab === 'calls' ? (
                  <CallsTab
                    calls={calls}
                    hasLoaded={callsLoaded}
                    actionInFlight={callActionInFlight}
                    onAcknowledge={handleAcknowledge}
                    onResolve={handleResolveCall}
                  />
                ) : (
                  <OrdersTab
                    orders={orders}
                    hasLoaded={ordersLoaded}
                    actionInFlight={orderActionInFlight}
                    onApprove={handleApproveOrder}
                    onReject={handleRejectOrder}
                  />
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Gizli sesler */}
      <audio ref={callAudioRef} src={waiterCallSound} preload="auto" />
      <audio ref={orderAudioRef} src={qrOrderSound} preload="auto" />

      <style>{`
        .inbox-scroll::-webkit-scrollbar { width: 8px; }
        .inbox-scroll::-webkit-scrollbar-track { background: transparent; }
        .inbox-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(156, 163, 175, 0.3);
          border-radius: 20px;
        }
        .dark .inbox-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(156, 163, 175, 0.2);
        }
      `}</style>
    </>
  );
};

// -------------------------------------------------------------------------
// Garson Çağrıları sekmesi
// -------------------------------------------------------------------------
interface CallsTabProps {
  calls: StaffWaiterCall[];
  /** İlk fetch tamamlandı mı (success/fail). Spinner sadece BU false'ken görünür. */
  hasLoaded: boolean;
  actionInFlight: Record<number, 'ack' | 'resolve' | undefined>;
  onAcknowledge: (id: number) => void;
  onResolve: (id: number) => void;
}

const CallsTab: React.FC<CallsTabProps> = React.memo(({
  calls,
  hasLoaded,
  actionInFlight,
  onAcknowledge,
  onResolve,
}) => {
  const { t } = useI18n();
  if (!hasLoaded) {
    return (
      <div className="flex justify-center items-center h-64">
        <PosLoader size="lg" label={t('common.loading')} />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <AnimatePresence>
        {calls.length === 0 && (
          <motion.div
            initial={{ opacity: 0, filter: 'blur(10px)' }}
            animate={{ opacity: 1, filter: 'blur(0px)' }}
            className="col-span-full py-20 flex flex-col items-center justify-center text-gray-400"
          >
            <div className="w-20 h-20 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center mb-6">
              <BellRing className="w-8 h-8 opacity-40" />
            </div>
            <p className="text-xl font-medium tracking-tight text-gray-500 dark:text-gray-400">
              {t('inbox.calls.emptyTitle')}
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
              {t('inbox.calls.emptyText')}
            </p>
          </motion.div>
        )}
        {calls.map((call) => {
          const isAcknowledged = call.status === 'acknowledged';
          const inflight = actionInFlight[call.call_id];
          return (
            <motion.div
              key={call.call_id}
              initial={{ opacity: 0, scale: 0.97, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: -12 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className={`pos-card relative overflow-hidden rounded-3xl p-6 md:p-8 transition-colors
                ${
                  isAcknowledged
                    ? 'bg-emerald-500/5 dark:bg-emerald-500/[0.03] ring-1 ring-emerald-500/30 dark:ring-emerald-500/20'
                    : 'bg-white dark:bg-[#111111] border border-black/5 dark:border-white/5 shadow-sm'
                }
              `}
            >
              {isAcknowledged && (
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 blur-[64px] rounded-full -mr-32 -mt-32 pointer-events-none" />
              )}

              <div className="flex justify-between items-start mb-8 relative z-10">
                <div className="flex items-center gap-4">
                  <div
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center backdrop-blur-xl ${
                      isAcknowledged
                        ? 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400'
                        : 'bg-black/5 text-gray-900 dark:bg-white/10 dark:text-white'
                    }`}
                  >
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                      {call.table?.name || `${t('common.table')} ${call.table?.id ?? ''}`}
                    </h3>
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400 mt-1">
                      <Clock className="w-4 h-4" />
                      {formatAgoTr(call.created_at, t)}
                    </div>
                    {call.reason_label && (
                      <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {call.reason_label}
                        {call.note ? ` • ${call.note}` : ''}
                      </div>
                    )}
                  </div>
                </div>

                <AnimatePresence mode="wait">
                  {!isAcknowledged ? (
                    <motion.div
                      key="pending"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 text-orange-600 dark:text-orange-400 text-xs font-semibold tracking-wide"
                    >
                      <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
                      {t('inbox.status.waiting')}
                    </motion.div>
                  ) : (
                    <motion.div
                      key="ack"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-semibold tracking-wide"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      {t('inbox.status.acknowledged')}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div className="relative z-10 w-full">
                <AnimatePresence mode="popLayout">
                  {isAcknowledged ? (
                    <motion.button
                      key="btn-resolve"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      disabled={!!inflight}
                      onClick={() => onResolve(call.call_id)}
                      className="w-full h-14 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-[15px] transition-all flex items-center justify-center gap-2 active:scale-[0.98] shadow-lg shadow-emerald-500/20 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {inflight === 'resolve' ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-5 h-5" />
                      )}
                      {t('inbox.completeAction')}
                    </motion.button>
                  ) : (
                    <motion.button
                      key="btn-ack"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      disabled={!!inflight}
                      onClick={() => onAcknowledge(call.call_id)}
                      className="w-full h-14 rounded-2xl bg-gray-900 hover:bg-black dark:bg-white dark:hover:bg-gray-100 text-white dark:text-black font-semibold text-[15px] transition-all flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {inflight === 'ack' ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Check className="w-5 h-5" />
                      )}
                      {t('inbox.onMyWay')}
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
});
CallsTab.displayName = 'CallsTab';

// -------------------------------------------------------------------------
// Müşteri Siparişleri sekmesi
// -------------------------------------------------------------------------
interface OrdersTabProps {
  orders: FlatOrder[];
  /** İlk fetch tamamlandı mı (success/fail). Spinner sadece BU false'ken görünür. */
  hasLoaded: boolean;
  actionInFlight: Record<number, 'accept' | 'reject' | undefined>;
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
}

const OrdersTab: React.FC<OrdersTabProps> = React.memo(({
  orders,
  hasLoaded,
  actionInFlight,
  onApprove,
  onReject,
}) => {
  const { t } = useI18n();
  if (!hasLoaded) {
    return (
      <div className="flex justify-center items-center h-64">
        <PosLoader size="lg" label={t('common.loading')} />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <AnimatePresence>
        {orders.length === 0 && (
          <motion.div
            initial={{ opacity: 0, filter: 'blur(10px)' }}
            animate={{ opacity: 1, filter: 'blur(0px)' }}
            className="col-span-full py-20 flex flex-col items-center justify-center text-gray-400"
          >
            <div className="w-20 h-20 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center mb-6">
              <UtensilsCrossed className="w-8 h-8 opacity-40" />
            </div>
            <p className="text-xl font-medium tracking-tight text-gray-500 dark:text-gray-400">
              {t('inbox.orders.emptyTitle')}
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
              {t('inbox.orders.emptyText')}
            </p>
          </motion.div>
        )}
        {orders.map((order) => {
          const inflight = actionInFlight[order.id];
          return (
            <motion.div
              key={order.id}
              initial={{ opacity: 0, scale: 0.97, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: -12 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="pos-card bg-white dark:bg-[#111111] rounded-3xl border border-black/5 dark:border-white/5 shadow-sm overflow-hidden flex flex-col"
            >
              {/* Order Header */}
              <div className="px-8 py-6 border-b border-black/5 dark:border-white/5 bg-gray-50/50 dark:bg-white/[0.02]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-600 dark:text-blue-400">
                      <UtensilsCrossed className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
                        {order.table_name}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        <Clock className="w-3.5 h-3.5" />
                        {formatHm(order.created_at) || formatAgoTr(order.created_at, t)}
                        <span className="w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600"></span>
                        <span className="text-blue-600 dark:text-blue-400 font-medium">
                          {t('inbox.newOrder')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                      {t('inbox.total')}
                    </div>
                    <div className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                      ₼{order.total.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="flex-1 p-8">
                <div className="space-y-4">
                  {order.items.length === 0 ? (
                    <p className="text-sm text-gray-400">{t('inbox.noItems')}</p>
                  ) : (
                    order.items.map((item, idx) => (
                      <div key={`${item.id}-${idx}`} className="flex justify-between items-center group">
                        <div className="flex items-center gap-4">
                          <span className="w-8 h-8 rounded-lg bg-black/5 dark:bg-white/10 flex items-center justify-center text-sm font-semibold text-gray-700 dark:text-gray-200">
                            {item.quantity}x
                          </span>
                          <span className="font-medium text-[15px] text-gray-800 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                            {item.name}
                            {item.type === 'set' && (
                              <span className="ml-2 text-[10px] uppercase tracking-wider text-blue-500/80">
                                {t('inbox.set')}
                              </span>
                            )}
                          </span>
                        </div>
                        <span className="font-medium text-gray-500 dark:text-gray-400">
                          ₼{(item.unit_price * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="px-8 pb-8 pt-4 flex gap-4">
                <button
                  type="button"
                  disabled={!!inflight}
                  onClick={() => onReject(order.id)}
                  className="flex-1 h-14 rounded-2xl bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10 text-gray-700 dark:text-gray-300 font-semibold text-[15px] transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {inflight === 'reject' ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                  {t('inbox.reject')}
                </button>
                <button
                  type="button"
                  disabled={!!inflight}
                  onClick={() => onApprove(order.id)}
                  className="flex-[2] h-14 rounded-2xl bg-[var(--primary)] hover:bg-orange-600 text-white font-semibold text-[15px] transition-all flex items-center justify-center gap-2 shadow-lg shadow-orange-500/20 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {inflight === 'accept' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-5 h-5" />
                  )}
                  {t('inbox.approve')}
                </button>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
});
OrdersTab.displayName = 'OrdersTab';

export default NotificationModal;
